import {customElement} from 'lit/decorators.js';
import {compose, equals, not, prop} from 'ramda';
import {of} from 'rxjs';
import {Action, Actor, Entity} from './actor';
import Decimal, {DecimalSource} from './break_infinity';
import {Cost, DynamicCost} from './interfaces/dynamic-value';
import {MAX_ACTORS, ri, RunnerIdle} from './runner-idle';
import {Gettable, ResourceName} from './types/runner-idle';
import {
  assignIfDefined,
  capitalizeFirstLetter,
  randFloat,
  randInt,
  readColor,
  writeColor,
} from './utils';
import {Vector} from './Vector';
import {Image} from 'p5';
import Color from 'colorjs.io';
export type Color = typeof Color;
import {CreatureTemplates} from './creature_templates';
import {StatusEffect} from './status-effect';

export const imageMap: Map<string, Image> = new Map();
export class Creature extends Actor {
  // #region Properties (6)

  breed!: Partial<Creature>;
  public heading: Vector = Vector.random();
  image!: Image;
  public imageHues: [Color, Color, Color] = [
    new Color('#ff0000'),
    new Color('#00ff00'),
    new Color('#0000ff'),
  ];
  public imageString = 'creature';
  public loot: Map<Gettable, DecimalSource> = new Map();

  // #endregion Properties (6)

  // #region Constructors (1)

  constructor() {
    super();
    this.resources.health = {
      name: 'health',
      value: new Decimal(1),
      capacity: new Decimal(10),
    };
    this.stats.strength = {
      name: 'strength',
      value: new Decimal(1),
    };
    this.stats.speed = {
      name: 'speed',
      value: new Decimal(1),
    };
  }

  // #endregion Constructors (1)

  // #region Public Static Methods (2)

  public static override create(
    overrides: Readonly<Partial<Creature>> = {}
  ): Creature {
    const instance = new Creature();
    instance.breed = overrides;
    const {stats, resources, imageHues, ...rest} = overrides;
    if (stats) {
      for (const [name, statOverrides] of Object.entries(stats)) {
        assignIfDefined(instance.stats[name], statOverrides);
      }
    }
    if (resources) {
      for (const [name, resourceOverrides] of Object.entries(resources)) {
        assignIfDefined(instance.resources[name], resourceOverrides);
      }
    }
    if (imageHues) {
      instance.imageHues = imageHues?.map((colorJSON) => {
        try {
          return new Color(colorJSON);
        } catch (e) {
          return new Color('white');
        }
      });
    }
    assignIfDefined(instance, {
      displayName: capitalizeFirstLetter(rest.name ?? instance.displayName),
      ...rest,
    });
    instance.actions = instance.makeActions();
    instance.deathActions = instance.makeDeathActions();
    instance.actions
      .filter(prop('properties'))
      .forEach((c) => assignIfDefined(instance, c.properties!));
    instance.deathActions
      .filter(prop('properties'))
      .forEach((c) => assignIfDefined(instance, c.properties!));
    // Place at position, or at random position near player if not specified
    instance.position$.next(
      overrides.position ??
        ri.position.plus(Vector.random(new Decimal(Math.random()).times(5000)))
    );
    return instance;
  }

  public static override fromJSON(jsonObject): Creature {
    if (CreatureTemplates.has(jsonObject.name)) {
      const instance = Creature.create({
        ...(CreatureTemplates.get(jsonObject.name) ?? {}),
        ...jsonObject,
        position: new Vector(...jsonObject.position),
        heading: new Vector(...jsonObject.heading),
        interactionRange: new Decimal(jsonObject.interactionRange),
        bodySize: new Decimal(jsonObject.bodySize),
        loot: new Map(
          Object.entries(jsonObject.loot).map(([key, value]) => [
            key,
            new Decimal(value),
          ])
        ),
        resources: Object.fromEntries(
          Object.entries(jsonObject.resources).map(([key, value]) => [
            key,
            {
              name: key,
              value: new Decimal(value.value),
              capacity: new Decimal(value.capacity),
            },
          ])
        ),
        stats: Object.fromEntries(
          Object.entries(jsonObject.stats).map(([key, value]) => [
            key,
            {
              name: key,
              value: new Decimal(value.value),
            },
          ])
        ),
      });
      return instance;
    }
    return Creature.create({});
  }

  // #endregion Public Static Methods (2)

  // #region Public Methods (6)

  /**
   * Adds amount to a specified resource.
   *
   * If the resource does not exist in this.resources,
   * creates it as empty with the given capacity (default amount),
   * then adds amount.
   */
  public addResource(
    resourceName: ResourceName,
    amount: DecimalSource,
    newCapacity: DecimalSource = amount
  ): void {
    if (!(resourceName in this.resources)) {
      this.resources[resourceName] = {
        name: resourceName,
        value: new Decimal(0),
        capacity: new Decimal(newCapacity),
      };
    }
    this.resources[resourceName]!.value = this.resources[
      resourceName
    ]!.value?.plus(amount).clamp(
      new Decimal(0),
      this.resources[resourceName]!.capacity! // terrible typing, jeez
    );
  }
  updateImage(p: import('p5')) {
    const imageKey = `${this.imageString} ${this.imageHues}`;
    // if (!this.image) {
    if (!imageMap.has(imageKey)) {
      const image = p.loadImage(`./images/${this.imageString}.png`, (image) => {
        image.loadPixels();
        const newR = this.imageHues[0];
        const newG = this.imageHues[1];
        const newB = this.imageHues[2];
        // Replace red channel with newR, etc.
        for (let y = 0; y < image.height; y++) {
          for (let x = 0; x < image.width; x++) {
            const [r, g, b, a] = readColor(image, x, y);
            const red = r * newR.r + g * newG.r + b * newB.r;
            const green = r * newR.g + g * newG.g + b * newB.g;
            const blue = r * newR.b + g * newG.b + b * newB.b;
            const alpha = a;
            writeColor(image, x, y, red, green, blue, alpha);
          }
        }
        image.updatePixels();
        imageMap.set(imageKey, image);
        this.image = imageMap.get(imageKey)!;
      });
    } else {
      this.image = imageMap.get(imageKey)!;
    }
    // }
  }

  public override draw(p: import('p5'), zoomScale: Decimal): void {
    super.draw(p, zoomScale);
    if (!this.image) {
      this.updateImage(p);
    }
    p.push();
    p.translate(
      this.drawPosition.x.div(zoomScale).toNumber(),
      this.drawPosition.y.div(zoomScale).toNumber()
    );
    p.rotate(this.heading.direction.toNumber());
    if (this.image) {
      const imageFactor = this.bodySize.div(zoomScale);
      p.image(
        this.image,
        -imageFactor.max(1).toNumber() / 2,
        -imageFactor.max(1).toNumber() / 2,
        imageFactor.toNumber(),
        imageFactor.toNumber(),
        0,
        0,
        32,
        32
      );
    }
    const mag: number = this.heading.magnitude
      .times(this.stats?.speed?.value ?? 1)
      .div(zoomScale)
      .toNumber();
    p.line(0, 0, mag, 0);
    p.line(mag, 0, mag - 2, 2);
    p.line(mag, 0, mag - 2, -2);
    p.pop();
  }

  public override interact(
    _distance: Decimal,
    entity: RunnerIdle | Creature = ri
  ): void {
    super.interact(_distance, entity);
    if (entity instanceof RunnerIdle) {
      // Player
      entity.takeDamage(this.stats.strength?.value ?? new Decimal(1), this);
      entity.dealDamage(this);
    } else if (
      ri.actors.length < MAX_ACTORS &&
      entity instanceof Creature &&
      this.name === entity.name
    ) {
      // Another same creature?
      ri.addActor(
        Creature.create({
          name: this.name,
        })
      );
    } else if (entity && this.name !== entity.name && 'takeDamage' in entity) {
      // Something else
      entity.takeDamage(this.stats.strength?.value ?? new Decimal(1), this);
    }
  }

  public override moveTo(nextPosition: Vector): boolean {
    if (super.moveTo(nextPosition)) {
      return true;
    }
    this.heading = this.heading.negate();
    return false;
  }

  public override takeDamage(damage: Decimal, attacker: Entity) {
    // console.log('takeDamage', damage, attacker.name, 'to', this.name);
    if (!this.resources.health) return;
    if (damage.gt(0)) {
      this.resources.health.value = this.resources.health.value?.minus(damage);
      // Was this creature killed?
      if (ri.actors && this.resources.health.value?.lte(0)) {
        this.dead$.next(true);
        // Do death action
        this.deathActions.forEach((action) => action.act.bind(this)());
        if ('kill' in attacker) {
          attacker.kill(this);
        }
        // Delete this creature
        ri.actors = ri.actors.filter(compose(not, equals(this as Actor)));
      }
    }
  }

  public override toJSON() {
    return {
      ...super.toJSON(),
      heading: this.heading,
      loot: this.loot,
      imageString: this.imageString,
      imageHues: this.imageHues,
    };
  }

  // #endregion Public Methods (6)
}
